import { useState } from "react"
import Icon from "../../components/Icon"
import { formatDateShort } from "../../utils/format"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { describeRetry } from "./autopayApprovalDisplay"

// What the billing company sees when a customer has not allowed automatic
// payment, with the choice to ask them. Nothing is sent unless the company taps
// "Ask to approve". Shown in a customer's statement and right after a bill.
function AutopayApprovalRequest({ approval, customerName, context = "statement", onRequest, onDismiss }) {
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState("")

  if (!approval || approval.status === "not_needed") return null
  const name = customerName || "This customer"
  const isBill = context === "bill"

  if (approval.status === "on" || approval.status === "trusted") {
    if (context !== "statement") return null
    return (
      <p className="flex items-center gap-1.5 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">
        <Icon name="shield-check" size="0.8rem" className="shrink-0" />
        Auto-pay on. Bills are paid from their wallet by themselves.
      </p>
    )
  }

  if (approval.status === "off") {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-700">
        <p className="font-bold text-slate-900">
          {isBill ? "Bill saved. Customer on Manual Payment." : "Auto-pay turned off"}
        </p>
        <p className="mt-0.5">{name} turned off auto-pay for your company. They can still pay your bills themselves.</p>
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="mt-2 text-[11px] font-bold text-slate-600 underline">
            OK
          </button>
        )}
      </div>
    )
  }

  const ask = async () => {
    if (sending) return
    setSending(true)
    setError("")
    try {
      await onRequest()
      setSent(true)
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not send the request. Please try again."))
    } finally {
      setSending(false)
    }
  }

  const waitingForRetry = !approval.canRequest && approval.retryAt

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
      <p className="flex items-center gap-1.5 font-bold">
        <Icon name="shield-exclamation" size="0.8rem" className="shrink-0" />
        {isBill ? "Bill saved. Customer on Manual Payment." : "Auto-pay not approved"}
      </p>
      <p className="mt-0.5">
        {isBill
          ? `${name} has not authorized Auto-Pay. Payment is pending until the customer pays or approves Auto-Pay.`
          : `${name} has not allowed your company to take payments from their wallet. Bills wait until they approve or pay.`}
      </p>

      {sent ? (
        <p role="status" className="mt-2 font-semibold text-emerald-800">
          Request sent. {name} will see it on their dashboard.
        </p>
      ) : waitingForRetry ? (
        <p className="mt-2 text-amber-800">
          You asked on {formatDateShort(approval.requestedAt)}. {describeRetry(approval.retryAt)}
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={ask}
            disabled={sending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
          >
            <Icon name="bell" size="0.7rem" />
            {sending ? "Sending…" : isBill ? "Request Auto-Pay Approval" : "Ask to approve"}
          </button>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-[11px] font-bold text-amber-800 transition-colors hover:bg-amber-100"
            >
              {isBill ? "Not Now" : "Not now"}
            </button>
          )}
        </div>
      )}

      {sent && onDismiss && (
        <button type="button" onClick={onDismiss} className="mt-1.5 text-[11px] font-bold text-amber-800 underline">
          Close
        </button>
      )}
      {error && <p role="alert" className="mt-2 font-medium text-red-700">{error}</p>}
    </div>
  )
}

export default AutopayApprovalRequest
